import { Box } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import { type FC, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ASSISTANT_AVATAR, WALKTHROUGH_GUIDE } from "@/theme/tokens";
import type { AvatarRect } from "../AssistantAvatar/assistantAvatarGeometry";
import { type GuideAim, guideAim, type WalkthroughGuide, WalkthroughGuideBeat } from "./walkthroughGuidePose";

type Props = {
	guide: WalkthroughGuide;
	beat: WalkthroughGuideBeat;
	targetSelector: string | null;
	tooltip: HTMLElement | null;
	stepKey: number;
};

const { TOUR_TRAVEL, EASE_ONE_SHOT } = ASSISTANT_AVATAR.MOTION;

const HOP_SX = {
	"@keyframes walkthroughGuideHop": {
		"0%": { transform: "translateY(0)" },
		"40%": { transform: `translateY(-${WALKTHROUGH_GUIDE.HOP_PX}px)` },
		"100%": { transform: "translateY(0)" },
	},
	animation: `walkthroughGuideHop ${TOUR_TRAVEL}ms ${EASE_ONE_SHOT}`,
};

const toRect = (rect: DOMRect): AvatarRect => ({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });

const sameAim = (a: GuideAim, b: GuideAim): boolean => a.point === b.point && a.gaze.x === b.gaze.x && a.gaze.y === b.gaze.y;

/** Re-aims on window resize/scroll (rAF-throttled), the target resizing, and joyride moving the card's floater. */
const useGuideAim = (figureRef: RefObject<HTMLElement | null>, targetSelector: string | null, tooltip: HTMLElement | null): GuideAim | null => {
	// Keyed by target so a new step never renders the previous target's aim.
	const [measured, setMeasured] = useState<{ selector: string; aim: GuideAim } | null>(null);
	const measure = useCallback(() => {
		if (targetSelector === null || figureRef.current === null) return;
		const target = document.querySelector(targetSelector);
		if (target === null) return;
		const next = guideAim(toRect(figureRef.current.getBoundingClientRect()), toRect(target.getBoundingClientRect()));
		setMeasured((current) => (current?.selector === targetSelector && sameAim(current.aim, next) ? current : { selector: targetSelector, aim: next }));
	}, [figureRef, targetSelector]);

	useLayoutEffect(measure, [measure]);
	useEffect(() => {
		let frame: number | null = null;
		const schedule = () => {
			if (frame !== null) return;
			frame = requestAnimationFrame(() => {
				frame = null;
				measure();
			});
		};
		window.addEventListener("resize", schedule);
		window.addEventListener("scroll", schedule, true);
		const resizeObserver = new ResizeObserver(schedule);
		const target = targetSelector === null ? null : document.querySelector(targetSelector);
		if (target !== null) resizeObserver.observe(target);
		const moveObserver = new MutationObserver(schedule);
		if (tooltip?.parentElement) moveObserver.observe(tooltip.parentElement, { attributes: true, attributeFilter: ["style"] });
		return () => {
			if (frame !== null) cancelAnimationFrame(frame);
			window.removeEventListener("resize", schedule);
			window.removeEventListener("scroll", schedule, true);
			resizeObserver.disconnect();
			moveObserver.disconnect();
		};
	}, [measure, targetSelector, tooltip]);
	return measured !== null && measured.selector === targetSelector ? measured.aim : null;
};

/** The step card's header slot: the guide hops in per step, points to the target's side and looks at it. */
export const WalkthroughGuideSlot: FC<Props> = ({ guide, beat, targetSelector, tooltip, stepKey }) => {
	const reducedMotion = useReducedMotion() === true;
	const figureRef = useRef<HTMLDivElement>(null);
	const aim = useGuideAim(figureRef, targetSelector, tooltip);
	return (
		<Box ref={figureRef} aria-hidden data-testid="walkthrough-guide" data-beat={beat} sx={{ width: guide.size, height: guide.size, mb: 1 }}>
			<Box key={stepKey} sx={reducedMotion ? undefined : HOP_SX}>
				{guide.render({
					beat,
					size: guide.size,
					point: aim === null || beat === WalkthroughGuideBeat.Done ? undefined : aim.point,
					gaze: aim === null ? undefined : aim.gaze,
				})}
			</Box>
		</Box>
	);
};
